// OGC-531 table row reordering (TYPE-ORDERING.md, prototype reorder.js).
// Pointer: press a grip and move 6px to pick the row up; it follows the
// pointer (a ghost shows its name), the page auto-scrolls near the edges and
// release drops it. Keyboard: Space picks up, ArrowUp/Down/Home/End move,
// Enter/Space drop, Escape/Tab cancel. A plain click on the grip is left to
// its Move menu. The order lives in React state while moving; commit reports
// (newIds, movedId, beforeIds) only when the order changed.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const DRAG_THRESHOLD = 6;
const EDGE = 70;

export function moveId(ids, id, toIndex) {
  const next = ids.filter((value) => value !== id);
  next.splice(Math.max(0, Math.min(toIndex, next.length)), 0, id);
  return next;
}

export function sameOrder(left = [], right = []) {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

// Index the moving row should take from the pointer's y: before the first
// other row whose middle is below the pointer, otherwise last.
export function pointerTargetIndex(otherRects, y) {
  const index = otherRects.findIndex((rect) => y < rect.top + rect.height / 2);
  return index < 0 ? otherRects.length : index;
}

export default function useRowReorder({ ids, onCommit, announce = () => {} }) {
  const [order, setOrder] = useState(null);
  const [active, setActive] = useState(null);
  const operation = useRef(null);
  const rowElements = useRef(new Map());
  const frame = useRef(0);
  const suppressClick = useRef(0);
  // Space activates a button on keyup: swallow the keyup of a Space that
  // picked up or dropped a row, so it does not open the Move menu.
  const suppressSpaceRelease = useRef(false);
  const handlers = useRef(null);
  const current = order || ids;

  function label(state) {
    const list = state.order;
    return `${state.label}, position ${list.indexOf(state.id) + 1} of ${list.length}.`;
  }

  function setOperationOrder(state, next) {
    if (sameOrder(state.order, next)) return false;
    state.order = next;
    setOrder(next);
    return true;
  }

  function cleanup(state) {
    cancelAnimationFrame(frame.current);
    frame.current = 0;
    state.ghost?.remove();
    document.body.classList.remove('is-row-dragging');
    if (state.kind === 'pointer') {
      try {
        if (state.handle?.hasPointerCapture?.(state.pointerId)) state.handle.releasePointerCapture(state.pointerId);
      } catch { /* already released */ }
    }
  }

  function finish(commit, { focus = true, silent = false } = {}) {
    const state = operation.current;
    if (!state) return;
    operation.current = null;
    cleanup(state);
    setActive(null);
    setOrder(null);
    if (focus && state.handle?.isConnected) state.handle.focus({ preventScroll: true });
    const changed = commit && !sameOrder(state.before, state.order);
    if (!silent && state.started) {
      announce(commit
        ? `${label(state)} ${changed ? 'Order updated.' : 'Order unchanged.'}`
        : `${state.label} reordering cancelled.`);
    }
    if (changed) onCommit?.(state.order, state.id, state.before);
  }

  function markStarted(state) {
    state.started = true;
    setActive(state.id);
    announce(`${state.label} picked up. ${label(state)} Use arrow keys to move, Enter to drop, or Escape to cancel.`);
  }

  function moveFromPointer(state) {
    const others = state.order.filter((id) => id !== state.id);
    const rects = others.map((id) => rowElements.current.get(id)?.getBoundingClientRect()).filter(Boolean);
    if (rects.length === others.length) {
      setOperationOrder(state, moveId(state.order, state.id, pointerTargetIndex(rects, state.y)));
    }
    if (state.ghost) {
      const left = Math.max(8, Math.min(state.x - state.offsetX, document.documentElement.clientWidth - state.ghostWidth - 8));
      state.ghost.style.left = `${left}px`;
      state.ghost.style.top = `${state.y - state.offsetY}px`;
    }
  }

  function tick() {
    const state = operation.current;
    if (!state || state.kind !== 'pointer' || !state.started) return;
    const height = window.innerHeight;
    const delta = state.y < EDGE
      ? -Math.ceil(14 * Math.min(1, (EDGE - state.y) / EDGE))
      : state.y > height - EDGE ? Math.ceil(14 * Math.min(1, (state.y - height + EDGE) / EDGE)) : 0;
    if (delta) window.scrollBy(0, delta);
    moveFromPointer(state);
    frame.current = requestAnimationFrame(tick);
  }

  function beginPointerDrag(state) {
    markStarted(state);
    const rect = rowElements.current.get(state.id)?.getBoundingClientRect() || { left: state.startX, top: state.startY, width: 280 };
    state.ghostWidth = Math.min(rect.width, 280, document.documentElement.clientWidth - 24);
    state.offsetX = Math.min(state.startX - rect.left, state.ghostWidth - 16);
    state.offsetY = Math.min(24, state.startY - rect.top);
    const ghost = document.createElement('div');
    ghost.className = 'type-drag-ghost';
    ghost.setAttribute('aria-hidden', 'true');
    const grip = state.handle?.querySelector('svg')?.cloneNode(true);
    if (grip) ghost.append(grip);
    const text = document.createElement('span');
    text.textContent = state.label;
    ghost.append(text);
    Object.assign(ghost.style, { position: 'fixed', width: `${state.ghostWidth}px`, pointerEvents: 'none', zIndex: '120' });
    document.body.append(ghost);
    state.ghost = ghost;
    document.body.classList.add('is-row-dragging');
    moveFromPointer(state);
    frame.current = requestAnimationFrame(tick);
  }

  handlers.current = { finish, beginPointerDrag, moveFromPointer };

  // React may move the row's DOM node when the order changes: re-capture the
  // pointer, or keep keyboard focus on the grip and its row in view.
  useLayoutEffect(() => {
    const state = operation.current;
    if (!state?.started) return;
    if (state.kind === 'pointer') {
      try { state.handle?.setPointerCapture?.(state.pointerId); } catch { /* cancelled */ }
    } else if (state.handle?.isConnected) {
      if (document.activeElement !== state.handle) state.handle.focus({ preventScroll: true });
      state.handle.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }
  }, [order]);

  useEffect(() => {
    function handleMove(event) {
      const state = operation.current;
      if (!state || state.kind !== 'pointer' || event.pointerId !== state.pointerId) return;
      state.x = event.clientX;
      state.y = event.clientY;
      if (!state.started && Math.hypot(state.x - state.startX, state.y - state.startY) >= DRAG_THRESHOLD) {
        handlers.current.beginPointerDrag(state);
      }
      if (state.started) {
        event.preventDefault();
        handlers.current.moveFromPointer(state);
      }
    }
    function handleUp(event) {
      const state = operation.current;
      if (!state || state.kind !== 'pointer' || event.pointerId !== state.pointerId) return;
      if (state.started) {
        event.preventDefault();
        suppressClick.current = performance.now() + 500;
        handlers.current.finish(true);
      } else {
        operation.current = null;
        cleanup(state);
      }
    }
    function handleCancel(event) {
      const state = operation.current;
      if (state?.kind === 'pointer' && event.pointerId === state.pointerId) {
        suppressClick.current = performance.now() + 500;
        handlers.current.finish(false);
      }
    }
    function handleKeyCapture(event) {
      if (event.key === 'Escape' && operation.current?.kind === 'pointer') {
        event.preventDefault();
        event.stopPropagation();
        suppressClick.current = performance.now() + 500;
        handlers.current.finish(false);
      }
    }
    function handleOutside(event) {
      const state = operation.current;
      if (state?.kind === 'keyboard' && !state.handle?.contains(event.target)) handlers.current.finish(false, { focus: false });
    }
    function handleKeyUpCapture(event) {
      if (event.key === ' ' && suppressSpaceRelease.current) {
        event.preventDefault();
        suppressSpaceRelease.current = false;
      }
    }
    function handleBlur() {
      if (operation.current) handlers.current.finish(false, { focus: false });
    }
    document.addEventListener('pointermove', handleMove, { passive: false });
    document.addEventListener('pointerup', handleUp);
    document.addEventListener('pointercancel', handleCancel);
    document.addEventListener('keydown', handleKeyCapture, true);
    document.addEventListener('keyup', handleKeyUpCapture, true);
    document.addEventListener('pointerdown', handleOutside, true);
    document.addEventListener('focusin', handleOutside);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      document.removeEventListener('pointercancel', handleCancel);
      document.removeEventListener('keydown', handleKeyCapture, true);
      document.removeEventListener('keyup', handleKeyUpCapture, true);
      document.removeEventListener('pointerdown', handleOutside, true);
      document.removeEventListener('focusin', handleOutside);
      window.removeEventListener('blur', handleBlur);
      if (operation.current) handlers.current.finish(false, { focus: false, silent: true });
    };
  }, []);

  function handleProps(id, rowLabel) {
    return {
      'aria-pressed': active === id ? 'true' : 'false',
      onPointerDown(event) {
        if (event.button !== 0 || event.isPrimary === false || event.currentTarget.disabled) return;
        // A new press is a new gesture: an earlier drop's click window ends.
        suppressClick.current = 0;
        if (operation.current) finish(false, { focus: false });
        const handle = event.currentTarget;
        operation.current = {
          kind: 'pointer', id, label: rowLabel, handle, before: [...current], order: [...current],
          pointerId: event.pointerId, started: false, startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY,
        };
        handle.focus({ preventScroll: true });
        try { handle.setPointerCapture(event.pointerId); } catch { /* document listeners still track */ }
      },
      // Runs before the Move menu's own key handling; a handled key is
      // prevented so the menu stays closed.
      onKeyDown(event) {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        const state = operation.current;
        if (!state) {
          if (event.key !== ' ' || event.repeat) return;
          event.preventDefault();
          suppressSpaceRelease.current = true;
          operation.current = { kind: 'keyboard', id, label: rowLabel, handle: event.currentTarget, before: [...current], order: [...current], started: false };
          markStarted(operation.current);
          return;
        }
        if (state.kind !== 'keyboard') return;
        if (event.key === 'Tab') { finish(false); return; }
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); finish(false); return; }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (event.repeat) return;
          if (event.key === ' ') suppressSpaceRelease.current = true;
          finish(true);
          return;
        }
        if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        const index = state.order.indexOf(id);
        const target = event.key === 'Home' ? 0
          : event.key === 'End' ? state.order.length - 1
            : Math.max(0, Math.min(state.order.length - 1, index + (event.key === 'ArrowUp' ? -1 : 1)));
        if (target === index || !setOperationOrder(state, moveId(state.order, id, target))) {
          announce(label(state));
          return;
        }
        announce(label(state));
      },
      // A click right after a pointer drop, or during a keyboard move,
      // belongs to that gesture, not to the Move menu.
      onClickCapture(event) {
        if (performance.now() < suppressClick.current || operation.current?.kind === 'keyboard') {
          suppressClick.current = 0;
          event.preventDefault();
          event.stopPropagation();
        }
      },
    };
  }

  function rowRef(id) {
    return (element) => {
      if (element) rowElements.current.set(id, element);
      else rowElements.current.delete(id);
    };
  }

  return { order: current, active, handleProps, rowRef, cancel: () => finish(false, { focus: false, silent: true }) };
}
